/*
 * Copyright (C) 2026 Discorch Contributors
 *
 * This file is part of Discorch.
 *
 * Discorch is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Discorch is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Discorch. If not, see <https://www.gnu.org/licenses/>.
 */

import { CheckboxWithState } from "@/components/studio/MessageView/CheckboxWithState";
import { JumpToMessageModal } from "@/components/studio/MessageView/JumpToMessageModal";
import { useSelection } from "@/lib/stores/selection";
import { cn } from "@/lib/utils";
import type { Message, ParsedAttachment, ProcessedChannel } from "@/types";
import { format } from "date-fns";
import { ExternalLink, Link2, Paperclip } from "lucide-react";
import { memo, useCallback, useMemo, useState } from "react";

interface MessageCardProps {
  message: Message;
  channel: ProcessedChannel;
  searchTerm?: string;
  isContext?: boolean;
  isMatch?: boolean;
  style?: React.CSSProperties;
  selectable?: boolean;
  "data-message-id"?: string;
}

interface MessageContentProps {
  message: Message;
  channel: ProcessedChannel;
  timestamp: Date;
  attachments: ParsedAttachment[];
  searchTerm?: string;
  isContext?: boolean;
  onJumpToMessage?: () => void;
}

function highlightText(text: string, searchTerm: string) {
  if (!searchTerm) return text;

  const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${escapedSearchTerm})`, "gi"));

  return parts.map((part, i) => {
    const isMatch = part.toLowerCase() === searchTerm.toLowerCase();
    return isMatch ? (
      <span key={i} className="bg-yellow-200 dark:bg-yellow-900">
        {part}
      </span>
    ) : (
      part
    );
  });
}

const MessageContent = memo(
  function MessageContent({
    message,
    timestamp,
    attachments,
    searchTerm = "",
    isContext = false,
    onJumpToMessage = () => {},
  }: MessageContentProps) {
    return (
      <>
        <header className="mb-2 flex items-center justify-between">
          <time
            dateTime={timestamp.toISOString()}
            className={cn(
              "text-sm",
              isContext ? "text-muted-foreground/75" : "text-muted-foreground",
            )}
            title={format(timestamp, "PPpp")}
          >
            {format(timestamp, "PP p")}
          </time>

          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onJumpToMessage();
              }}
              className={cn(
                "hover:bg-accent/50 flex cursor-pointer items-center gap-1 rounded border px-2 py-0.5 text-xs",
                isContext
                  ? "text-muted-foreground/75"
                  : "text-muted-foreground",
              )}
              title="Jump to Message in Discord"
            >
              <Link2 className="h-3 w-3" />
              Jump
            </button>
            <span
              className={cn(
                "text-xs select-all",
                isContext
                  ? "text-muted-foreground/75"
                  : "text-muted-foreground",
              )}
              title="Message ID"
            >
              {message.ID}
            </span>
          </div>
        </header>

        <main className="overflow-hidden">
          {!message.Contents && !message.Attachments && (
            <p
              className={cn(
                isContext
                  ? "text-muted-foreground/75"
                  : "text-muted-foreground",
              )}
            >
              This message has no content or attachments. Use the Jump button to
              view in Discord.
            </p>
          )}

          {message.Contents && (
            <p
              className={cn(
                "break-all whitespace-pre-wrap",
                isContext && "text-foreground/90 line-clamp-2",
              )}
            >
              {searchTerm
                ? highlightText(message.Contents, searchTerm)
                : message.Contents}
            </p>
          )}

          {attachments.length > 0 && (
            <footer className="mt-2 space-y-1">
              <div className="text-muted-foreground flex items-center gap-1 text-xs">
                <Paperclip className="h-3 w-3" />
                <span>
                  {attachments.length} attachment
                  {attachments.length !== 1 && "s"}
                </span>
              </div>
              {(!isContext || attachments.length === 1) && (
                <ul className="space-y-1">
                  {attachments.map((attachment, idx) => (
                    <li key={idx}>
                      <a
                        href={attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary flex items-center gap-1 text-sm hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" />
                        {attachment.filename}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </footer>
          )}
        </main>
      </>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.message.ID === nextProps.message.ID &&
      prevProps.searchTerm === nextProps.searchTerm &&
      prevProps.isContext === nextProps.isContext
    );
  },
);

export const MessageCard = memo(
  function MessageCard({
    message,
    channel,
    searchTerm = "",
    isContext = false,
    isMatch = false,
    style,
    selectable = false,
    "data-message-id": messageId,
  }: MessageCardProps) {
    const [isJumpToMessageOpen, setIsJumpToMessageOpen] = useState(false);
    const { toggleMessage, isMessageSelected } = useSelection();

    const attachments = useMemo(
      () => message.parsedAttachments || [],
      [message.parsedAttachments],
    );

    const timestamp = useMemo(
      () => new Date(message.Timestamp),
      [message.Timestamp],
    );
    const isEmpty = !message.Contents && attachments.length === 0;

    const handleSelect = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        toggleMessage(channel.id, message.ID);
      },
      [channel.id, message.ID, toggleMessage],
    );

    const handleJumpToMessage = useCallback(() => {
      setIsJumpToMessageOpen(true);
    }, []);

    return (
      <>
        <div
          className={cn(
            "group overflow-hidden border p-4",
            isContext && "bg-muted/30",
            isMatch && "bg-yellow-50 shadow-xs dark:bg-yellow-900/20",
          )}
          style={style}
          data-message-id={messageId}
        >
          <div className="flex gap-3">
            {selectable && (
              <div
                className="flex h-full items-start pt-1"
                onClick={handleSelect}
              >
                <CheckboxWithState
                  checked={isMessageSelected(channel.id, message.ID)}
                  className={cn(
                    "h-4 w-4",
                    isContext && "opacity-75 hover:opacity-100",
                  )}
                  aria-label="Select message"
                />
              </div>
            )}

            <div className="min-w-0 flex-1">
              <MessageContent
                message={message}
                channel={channel}
                timestamp={timestamp}
                attachments={attachments}
                searchTerm={searchTerm}
                isContext={isContext}
                onJumpToMessage={handleJumpToMessage}
              />
            </div>
          </div>
        </div>

        <JumpToMessageModal
          isOpen={isJumpToMessageOpen}
          onClose={() => setIsJumpToMessageOpen(false)}
          channel={channel}
          message={message}
          isPotentiallyHidden={isEmpty}
        />
      </>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.message.ID === nextProps.message.ID &&
      prevProps.searchTerm === nextProps.searchTerm &&
      prevProps.isContext === nextProps.isContext &&
      prevProps.isMatch === nextProps.isMatch &&
      prevProps.selectable === nextProps.selectable
    );
  },
);

MessageCard.displayName = "MessageCard";
